import React, { useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/navbar/Navbar";
import "./style.scss";
import axios from "axios";
import { AuthContext } from "../../context/authContext/AuthContext";
import { Alert, Snackbar, Switch, TextField, Button } from "@mui/material";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

const RegisteredByTeacher = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "info" });
  const openToast = (msg, severity = "info") => setToast({ open: true, msg, severity });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ fullname: "", email: "", course: "", semester: "" });
  const [tEditId, setTEditId] = useState(null);
  const [tEditForm, setTEditForm] = useState({ fullname: "", email: "", course: "", semester: "" });

  // New student form
  const [form, setForm] = useState({ fullname: "", email: "", password: "", course: "", semester: "" });
  // New teacher form
  const [tForm, setTForm] = useState({ fullname: "", email: "", password: "", course: "ALL", semester: "ALL" });

  const isDefaultTeacher = !!user?.isAdmin; // default teacher has isAdmin true
  const currentUserId = user?._id || user?.id; // normalize id reference

  const authHeader = useMemo(
    () => ({ headers: { token: "Bearer " + user?.accessToken, Authorization: "Bearer " + user?.accessToken } }),
    [user?.accessToken]
  );

  const loadConfig = async () => {
    try {
      const res = await axios.get("/api/config/registration");
      setRegistrationOpen(!!res.data?.registrationOpen);
    } catch (e) {
      // default: true
    }
  };

  const toggleRegistration = async (nextVal) => {
    try {
      setLoading(true);
      await axios.put("/api/config/registration", { registrationOpen: nextVal }, authHeader);
      setRegistrationOpen(nextVal);
      openToast(`Registration ${nextVal ? "enabled" : "disabled"}`, "success");
    } catch (e) {
      openToast("Failed to update setting", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const res = await axios.get("/api/user?role=student", authHeader);
      setStudents(res.data || []);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] loadStudents error:", status, msg);
      openToast(`Failed to load students (${status || ""})`, "error");
    }
  };
  const loadTeachers = async () => {
    try {
      const res = await axios.get("/api/user?role=teacher", authHeader);
      setTeachers(res.data || []);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] loadTeachers error:", status, msg);
      openToast(`Failed to load teachers (${status || ""})`, "error");
    }
  };

  const startEdit = (s) => {
    setEditId(s._id);
    setEditForm({ fullname: s.fullname || "", email: s.email || "", course: s.course || "", semester: s.semester || "" });
  };
  const startTeacherEdit = (t) => {
    setTEditId(t._id);
    setTEditForm({ fullname: t.fullname || "", email: t.email || "", course: t.course || "", semester: t.semester || "" });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({ fullname: "", email: "", course: "", semester: "" });
  };
  const cancelTeacherEdit = () => {
    setTEditId(null);
    setTEditForm({ fullname: "", email: "", course: "", semester: "" });
  };

  const saveEdit = async () => {
    try {
      setLoading(true);
      await axios.put(`/api/user/${editId}`, { fullname: editForm.fullname, email: editForm.email, course: editForm.course, semester: editForm.semester }, authHeader);
      openToast("Student updated", "success");
      setEditId(null);
      await loadStudents();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] saveEdit error:", status, msg);
      if (status === 409) openToast("Email already exists", "error");
      else openToast(`Failed to update (${status || ""})`, "error");
    } finally {
      setLoading(false);
    }
  };
  const saveTeacherEdit = async () => {
    try {
      setLoading(true);
      await axios.put(`/api/user/${tEditId}`, { fullname: tEditForm.fullname, email: tEditForm.email, course: tEditForm.course, semester: tEditForm.semester }, authHeader);
      openToast("Teacher updated", "success");
      setTEditId(null);
      await loadTeachers();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] saveTeacherEdit error:", status, msg);
      if (status === 409) openToast("Email already exists", "error");
      else if (status === 403) openToast("Only the default teacher can manage other teacher accounts.", "error");
      else openToast(`Failed to update (${status || ""})`, "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteStudent = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`/api/user/${id}`, authHeader);
      openToast("Student deleted", "success");
      await loadStudents();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] deleteStudent error:", status, msg);
      openToast(`Failed to delete (${status || ""})`, "error");
    } finally {
      setLoading(false);
    }
  };
  const deleteTeacher = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`/api/user/${id}`, authHeader);
      openToast("Teacher deleted", "success");
      await loadTeachers();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] deleteTeacher error:", status, msg);
      if (status === 403) openToast("Only the default teacher can delete teacher accounts.", "error");
      else openToast(`Failed to delete (${status || ""})`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    const { fullname, email, password, course, semester } = form;
    if (!(fullname && email && password && course && semester)) {
      openToast("Please fill all fields", "error");
      return;
    }
    try {
      setLoading(true);
      await axios.post("/api/auth/register-by-teacher", { fullname, email, password, course, semester }, authHeader);
      openToast("Student created", "success");
      setForm({ fullname: "", email: "", password: "", course: "", semester: "" });
      loadStudents();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] createStudent error:", status, msg);
      if (status === 409) openToast("Email already exists", "error");
      else openToast(`Failed to create student (${status || ""})`, "error");
    } finally {
      setLoading(false);
    }
  };
  const handleCreateTeacher = async (e) => {
    e.preventDefault();
    const { fullname, email, password, course, semester } = tForm;
    if (!(fullname && email && password && course && semester)) {
      openToast("Fill all teacher fields", "error");
      return;
    }
    if (!isDefaultTeacher) {
      openToast("Only the default teacher can create other teacher accounts.", "error");
      return;
    }
    try {
      setLoading(true);
      await axios.post("/api/auth/register-by-teacher", { fullname, email, password, course, semester, isTeacher: true }, authHeader);
      openToast("Teacher created", "success");
      setTForm({ fullname: "", email: "", password: "", course: "ALL", semester: "ALL" });
      loadTeachers();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data || e?.message || "Unknown error";
      console.error("[RBT] createTeacher error:", status, msg);
      if (status === 409) openToast("Email already exists", "error");
      else if (status === 403) openToast("Only the default teacher can create other teacher accounts.", "error");
      else openToast(`Failed to create teacher (${status || ""})`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadStudents();
    loadTeachers();
  }, []);

  return (
    <div className="registered-by-teacher">
      <Navbar />
      <div className="container container--full">
        <div className="rbt-header">
          <h2>Registration Control</h2>
          <div className="toggle">
            <span className={!registrationOpen ? "off" : ""}>Off</span>
            <Switch
              color="primary"
              checked={registrationOpen}
              onChange={(e) => toggleRegistration(e.target.checked)}
              inputProps={{ "aria-label": "toggle registration" }}
            />
            <span className={registrationOpen ? "on" : ""}>On</span>
          </div>
        </div>

        <div className="create-student card">
          <h3>Create Student Manually</h3>
          <form onSubmit={handleCreateStudent}>
            <div className="grid">
              <TextField label="Full name" fullWidth value={form.fullname} onChange={(e)=>setForm({ ...form, fullname: e.target.value })} />
              <TextField label="Email" fullWidth value={form.email} onChange={(e)=>setForm({ ...form, email: e.target.value })} />
              <TextField label="Password" type="password" fullWidth value={form.password} onChange={(e)=>setForm({ ...form, password: e.target.value })} />
              <TextField label="Course" fullWidth value={form.course} onChange={(e)=>setForm({ ...form, course: e.target.value })} />
              <TextField label="Semester" fullWidth value={form.semester} onChange={(e)=>setForm({ ...form, semester: e.target.value })} />
            </div>
            <div className="actions">
              <Button variant="contained" type="submit" disabled={loading} style={{ textTransform: "none" }}>Create</Button>
            </div>
          </form>
        </div>

        {isDefaultTeacher ? (
          <div className="create-teacher card">
            <h3>Create Teacher Manually</h3>
            <form onSubmit={handleCreateTeacher}>
              <div className="grid">
                <TextField label="Full name" fullWidth value={tForm.fullname} onChange={(e)=>setTForm({ ...tForm, fullname: e.target.value })} />
                <TextField label="Email" fullWidth value={tForm.email} onChange={(e)=>setTForm({ ...tForm, email: e.target.value })} />
                <TextField label="Password" type="password" fullWidth value={tForm.password} onChange={(e)=>setTForm({ ...tForm, password: e.target.value })} />
                <TextField label="Course" fullWidth value={tForm.course} onChange={(e)=>setTForm({ ...tForm, course: e.target.value })} />
                <TextField label="Semester" fullWidth value={tForm.semester} onChange={(e)=>setTForm({ ...tForm, semester: e.target.value })} />
              </div>
              <div className="actions">
                <Button variant="contained" type="submit" disabled={loading} style={{ textTransform: "none" }}>Create Teacher</Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="create-teacher card disabled-info">
            <h3>Teacher Creation Restricted</h3>
            <p style={{ margin: 0, fontSize: 14 }}>Only the default teacher can create other teacher accounts.</p>
          </div>
        )}

        <div className="students card">
          <h3>All Students</h3>
          <div className="list">
            <div className="row head">
              <div>Name</div>
              <div>Email</div>
              <div>Course</div>
              <div>Semester</div>
              <div>Created</div>
              <div className="actions">Actions</div>
            </div>
            {students.map((s) => (
              <div className="row" key={s._id}>
                <div className="cell" data-label="Name">
                  {editId === s._id ? (
                    <TextField size="small" value={editForm.fullname} onChange={(e)=> setEditForm({ ...editForm, fullname: e.target.value })} />
                  ) : (
                    s.fullname
                  )}
                </div>
                <div className="cell" data-label="Email">
                  {editId === s._id ? (
                    <TextField size="small" value={editForm.email} onChange={(e)=> setEditForm({ ...editForm, email: e.target.value })} />
                  ) : (
                    s.email
                  )}
                </div>
                <div className="cell" data-label="Course">
                  {editId === s._id ? (
                    <TextField size="small" value={editForm.course} onChange={(e)=> setEditForm({ ...editForm, course: e.target.value })} />
                  ) : (
                    s.course
                  )}
                </div>
                <div className="cell" data-label="Semester">
                  {editId === s._id ? (
                    <TextField size="small" value={editForm.semester} onChange={(e)=> setEditForm({ ...editForm, semester: e.target.value })} />
                  ) : (
                    s.semester
                  )}
                </div>
                <div className="cell" data-label="Created">{new Date(s.createdAt).toLocaleString()}</div>
                <div className="actions">
                  {editId === s._id ? (
                    <>
                      <Button variant="contained" size="small" onClick={saveEdit} style={{ textTransform: "none", marginRight: 8 }}>Save</Button>
                      <Button variant="outlined" size="small" onClick={cancelEdit} style={{ textTransform: "none" }}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="text" size="small" onClick={() => startEdit(s)} title="Edit"><EditIcon /></Button>
                      <Button variant="text" size="small" color="error" onClick={() => deleteStudent(s._id)} title="Delete"><DeleteIcon /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="teachers card">
          <h3>All Teachers</h3>
          <div className="list">
            <div className="row head">
              <div>Name</div>
              <div>Email</div>
              <div>Course</div>
              <div>Semester</div>
              <div>Created</div>
              <div className="actions">Actions</div>
            </div>
            {teachers.map((t) => (
              <div className="row" key={t._id}>
                <div className="cell" data-label="Name">
                  {tEditId === t._id ? (
                    <TextField size="small" value={tEditForm.fullname} onChange={(e)=> setTEditForm({ ...tEditForm, fullname: e.target.value })} />
                  ) : (
                    t.fullname
                  )}
                </div>
                <div className="cell" data-label="Email">
                  {tEditId === t._id ? (
                    <TextField size="small" value={tEditForm.email} onChange={(e)=> setTEditForm({ ...tEditForm, email: e.target.value })} />
                  ) : (
                    t.email
                  )}
                </div>
                <div className="cell" data-label="Course">
                  {tEditId === t._id ? (
                    <TextField size="small" value={tEditForm.course} onChange={(e)=> setTEditForm({ ...tEditForm, course: e.target.value })} />
                  ) : (
                    t.course
                  )}
                </div>
                <div className="cell" data-label="Semester">
                  {tEditId === t._id ? (
                    <TextField size="small" value={tEditForm.semester} onChange={(e)=> setTEditForm({ ...tEditForm, semester: e.target.value })} />
                  ) : (
                    t.semester
                  )}
                </div>
                <div className="cell" data-label="Created">{new Date(t.createdAt).toLocaleString()}</div>
                <div className="actions">
                  {tEditId === t._id ? (
                    <>
                      <Button variant="contained" size="small" onClick={saveTeacherEdit} style={{ textTransform: "none", marginRight: 8 }}>Save</Button>
                      <Button variant="outlined" size="small" onClick={cancelTeacherEdit} style={{ textTransform: "none" }}>Cancel</Button>
                    </>
                  ) : (
                    // Show actions only if default teacher OR row is current logged in teacher (self-edit allowed)
                    (isDefaultTeacher) && (
                      <>
                        <Button variant="text" size="small" onClick={() => startTeacherEdit(t)} title="Edit"><EditIcon /></Button>
                        {t._id !== currentUserId && (
                          <Button variant="text" size="small" color="error" onClick={() => deleteTeacher(t._id)} title="Delete"><DeleteIcon /></Button>
                        )}
                      </>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Snackbar open={toast.open} autoHideDuration={2500} onClose={closeToast} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={closeToast} severity={toast.severity} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RegisteredByTeacher;
